import { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Upload,
  message,
  Row,
  Col,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import apiService from '../api/apiService';
import { Sidebar } from 'lucide-react';

const HotelDetails = () => {
  const hotelId = localStorage.getItem('hotel');
  console.log(hotelId);
  const [hotelDetails, setHotelDetails] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);

  const fetchHotelDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/hotels/?name');
      setHotelDetails(response || []);
    } catch (error) {
      message.error('Failed to fetch hotel details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotelDetails();
  }, []);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setLogoFile(null);
    setIsModalOpen(true);
  };

  const handleEdit = record => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      description: record.description === 'null' ? '' : record.description,
    });
    setLogoFile(record.logo || null);
    setIsModalOpen(true);
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: 'Are you sure you want to delete this hotel detail?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await apiService.delete(`/hotels/${id}/`);
          message.success('Hotel detail deleted successfully.');
          fetchHotelDetails();
        } catch (error) {
          message.error('Failed to delete hotel detail.');
        }
      },
    });
  };

  const handleSubmit = async values => {
    try {
      const formData = new FormData();
      for (const key in values) {
        formData.append(key, values[key]);
      }
      if (logoFile && typeof logoFile !== 'string') {
        formData.append('logo', logoFile);
      }

      if (editingRecord) {
        await apiService.put(`/hotels/${editingRecord.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Hotel detail updated successfully.');
      } else {
        await apiService.post('/hotels/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Hotel detail created successfully.');
      }
      setIsModalOpen(false);
      fetchHotelDetails();
    } catch (error) {
      console.error(error);
      message.error('Failed to save hotel detail.');
    }
  };

  const handleLogoChange = info => {
    if (info.file.status === 'done' || info.file.status === 'uploading') {
      setLogoFile(info.file.originFileObj);
    }
  };

  const columns = [
    {
      title: 'Logo',
      dataIndex: 'logo',
      render: logo =>
        logo ? (
          <img
            src={logo}
            alt="Logo"
            style={{ width: 50, height: 50, objectFit: 'cover' }}
          />
        ) : (
          '-'
        ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'City',
      dataIndex: 'city',
    },
    {
      title: 'State',
      dataIndex: 'state',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Website',
      dataIndex: 'website',
      render: text =>
        text && text !== 'http://null.com' ? (
          <a href={text} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      render: active => (active ? 'Yes' : 'No'),
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Hotel Details</h2>
          <Button type="primary" onClick={handleAdd}>
            Add Hotel
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={hotelDetails}
          rowKey="id"
          loading={loading}
          pagination={false}
          bordered
          scroll={{ x: 'max-content' }}
        />

        <Modal
          title={editingRecord ? 'Edit Hotel Detail' : 'Add Hotel Detail'}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onOk={() => form.submit()}
          destroyOnClose
          width={800}
          style={{
            top: 20,
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ is_active: true }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="address"
                  label="Address"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="city"
                  label="City"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="state"
                  label="State"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="country"
                  label="Country"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="zip_code"
                  label="Zip Code"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Phone"
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[{ required: true, type: 'email' }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="website" label="Website">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="is_active"
                  label="Active"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="description" label="Description">
                  <Input.TextArea
                    rows={3}
                    placeholder="Enter hotel description"
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item label="Logo">
                  <Upload
                    accept="image/*"
                    beforeUpload={() => false}
                    onChange={handleLogoChange}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>Select Logo</Button>
                  </Upload>
                  {logoFile && (
                    <div className="mt-2">
                      {typeof logoFile === 'string' ? (
                        <img
                          src={logoFile}
                          alt="Logo Preview"
                          style={{
                            width: 100,
                            height: 100,
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <img
                          src={URL.createObjectURL(logoFile)}
                          alt="Logo Preview"
                          style={{
                            width: 100,
                            height: 100,
                            objectFit: 'cover',
                          }}
                        />
                      )}
                    </div>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default HotelDetails;
